/**
 * TeamUsagePanel: what the team's agents have used. A 30-day SUMMARY first (so it stays the same size however long the team has been going),
 * then recent runs ten at a time, loaded on request. Questions and answers are never shown.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { spacesAPI } from '../../services/api.js';
import { T, btn, Pill, Section, kTokens, ago } from './teamUi.jsx';

const PAGE = 10;

const Tile = ({ label, value, sub }) => (
  <div style={{ flex: '1 1 150px', border: `1px solid ${T.border}`, borderRadius: 12, padding: '11px 14px', background: T.surface2 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color: T.ink, margin: '2px 0 1px', letterSpacing: '-0.02em' }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: T.ink3 }}>{sub}</div>}
  </div>
);

function Bars({ title, rows, unit }) {
  const max = Math.max(1, ...rows.map((r) => r.tokens));
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.ink2, marginBottom: 8 }}>{title}</div>
      {rows.length === 0 && <div style={{ fontSize: 12, color: T.ink4 }}>Nothing yet.</div>}
      {rows.map((r) => (
        <div key={r.name} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: T.ink }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}{r.mine ? ' (you)' : ''}</span>
            <span style={{ color: T.ink3, flexShrink: 0 }}>{kTokens(r.tokens)} · {r.runs} run{r.runs === 1 ? '' : 's'}</span>
          </div>
          <div style={{ height: 5, borderRadius: 99, background: T.surface3, marginTop: 3 }}>
            <div style={{ width: `${Math.max(3, (r.tokens / max) * 100)}%`, height: '100%', borderRadius: 99, background: r.mine ? T.accent : T.accentMid }} />
          </div>
        </div>
      ))}
      {unit && <div style={{ fontSize: 11, color: T.ink4 }}>{unit}</div>}
    </div>
  );
}

const payerLabel = (u) => (u.payerKind === 'owner' ? `${u.payer} paid` : u.payerKind === 'sponsor' ? `sponsored by ${u.payer}` : `${u.payer} paid (asked)`);

export default function TeamUsagePanel({ spaceId }) {
  const [summary, setSummary] = useState(null);
  const [sponsored, setSponsored] = useState(null);
  const [runs, setRuns] = useState([]);
  const [more, setMore] = useState({ hasMore: false, next: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const loaded = useRef(0);

  // The first page (and the summary). Refreshed now and then; once the person has paged further, only the summary is refreshed so their list
  // does not jump back.
  const loadFirst = useCallback(async () => {
    try {
      const d = await spacesAPI.usage(spaceId, { limit: PAGE });
      setSummary(d.summary); setSponsored(d.sponsored); setFailed(false);
      if (loaded.current <= PAGE) { setRuns(d.runs); loaded.current = d.runs.length; setMore({ hasMore: d.hasMore, next: d.nextBefore }); }
    } catch { setFailed(true); }
  }, [spaceId]);
  useEffect(() => {
    loadFirst();
    const t = setInterval(loadFirst, 30000);
    const onChange = () => loadFirst();
    window.addEventListener('lyra:roster-update', onChange);
    return () => { clearInterval(t); window.removeEventListener('lyra:roster-update', onChange); };
  }, [loadFirst]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      const d = await spacesAPI.usage(spaceId, { limit: PAGE, before: more.next });
      setRuns((r) => [...r, ...d.runs]); loaded.current += d.runs.length;
      setMore({ hasMore: d.hasMore, next: d.nextBefore });
    } catch { /* the button stays; try again */ }
    finally { setLoadingMore(false); }
  };
  const showLess = () => { setRuns((r) => r.slice(0, PAGE)); loaded.current = Math.min(loaded.current, PAGE); setMore({ hasMore: true, next: runs[PAGE - 1]?.at || null }); };

  return (
    <Section title="Usage" hint="The last 30 days. Who asked, whose agent answered and who covered the tokens. Questions and answers are never shown here.">
      {failed && !summary && <div style={{ padding: '4px 20px 16px', fontSize: 12.5, color: T.ink3 }}>Could not load usage right now.</div>}
      {!summary && !failed && <div style={{ padding: '4px 20px 16px', fontSize: 12.5, color: T.ink3 }}>Loading…</div>}
      {summary && summary.runs === 0 && <div style={{ padding: '4px 20px 18px', fontSize: 12.5, color: T.ink3 }}>No agent runs in the last 30 days.</div>}
      {summary && summary.runs > 0 && (
        <div style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Tile label="Runs" value={summary.runs.toLocaleString()} />
            <Tile label="Tokens" value={kTokens(summary.tokens)} sub={summary.costUSD ? `about $${summary.costUSD.toFixed(2)}` : undefined} />
            <Tile label="You covered" value={kTokens(summary.paidByMe.tokens)} sub={`${summary.paidByMe.runs} run${summary.paidByMe.runs === 1 ? '' : 's'}`} />
            {sponsored && <Tile label="Credits earned" value={sponsored.credits.toLocaleString()} sub={`${sponsored.runs} sponsored · ${sponsored.creditsToday}/${sponsored.dailyCap} today · no cash value yet`} />}
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Bars title="Who covered the tokens" rows={summary.byPayer} />
            <Bars title="Most used agents" rows={summary.byAgent} />
          </div>
        </div>
      )}

      {runs.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          <div style={{ padding: '12px 20px 4px', fontSize: 12, fontWeight: 700, color: T.ink2 }}>Recent runs</div>
          {runs.map((u) => (
            <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 20px', fontSize: 12.5, borderTop: `1px solid ${T.border}` }}>
              <span style={{ width: 74, flexShrink: 0, color: T.ink4, fontSize: 11.5 }}>{ago(u.at)}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.ink }}>
                <b>{u.agentName}</b>{u.requester ? <span style={{ color: T.ink3 }}> for {u.requester}</span> : null}
              </span>
              {u.status === 'capped' && <Pill tone="bad">hit its limit</Pill>}
              <Pill tone={u.payerKind === 'owner' ? 'plain' : 'accent'}>{payerLabel(u)}</Pill>
              <span style={{ width: 92, textAlign: 'right', flexShrink: 0, color: T.ink3 }}>{u.tokens.toLocaleString()} tokens{u.credits ? ` · +${u.credits}` : ''}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 20px', borderTop: `1px solid ${T.border}` }}>
            {more.hasMore && <button type="button" style={btn('plain')} disabled={loadingMore} onClick={showMore}>{loadingMore ? 'Loading…' : 'Show more'}</button>}
            {runs.length > PAGE && <button type="button" style={btn('plain')} onClick={showLess}>Show less</button>}
            {!more.hasMore && runs.length <= PAGE && <span style={{ fontSize: 11.5, color: T.ink4 }}>That is everything.</span>}
          </div>
        </div>
      )}
    </Section>
  );
}
